import React, { FC, useMemo, useState } from 'react';

import { MetadataAttributeDatatypeEnum } from '@ariksa/inventory-core/api';
import {
  Box,
  Collapse,
  Grid,
  GridItem,
  HStack,
  Icon,
  ScaleFade,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { isArray, isPlainObject, map, orderBy } from 'lodash';
import { FiChevronDown } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { Tags } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetadataField/Tags';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';

import { CheckBadges } from './CheckBadges';
import { DynamicObjectListTable } from './DynamicObjectListTable';
import { SummaryLabel as Label } from './SummaryLabel';
import { SummaryValue } from './SummaryValue';
import {
  DEFAULT_SECTION,
  DynamicAttribute,
  SummarySection,
  buildSections,
  isEmptyValue,
  sectionSummary,
} from './utils';

const OPEN_SECTIONS_KEY = 'resource-summary-open-sections';
const DEFAULT_OPEN = [DEFAULT_SECTION, 'Security checks'];

const readOpenSections = (): string[] => {
  try {
    const saved = window.localStorage.getItem(OPEN_SECTIONS_KEY);
    return saved ? JSON.parse(saved) : DEFAULT_OPEN;
  } catch {
    return DEFAULT_OPEN;
  }
};

const saveOpenSections = (sections: string[]) => {
  try {
    window.localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(sections));
  } catch {
    // Storage can be unavailable (private mode); the default still works.
  }
};

const isObjectList = value =>
  isArray(value) && value.length > 0 && value.every(isPlainObject);

const Field: FC<{ attribute: DynamicAttribute; provider?: string }> = ({
  attribute,
  provider,
}) => {
  const value = attribute.attribute_value;
  if (attribute.datatype === MetadataAttributeDatatypeEnum.Tags) {
    return (
      <GridItem colSpan={4}>
        <Tags
          display_name={attribute.display_name}
          tags={map(value, t => {
            const [entries] = Object.entries(t);
            return { key: entries?.[0], value: entries?.[1] };
          })}
        />
      </GridItem>
    );
  }
  if (isObjectList(value) && !attribute.hyperlink) {
    return (
      <GridItem colSpan={4}>
        <DynamicObjectListTable label={attribute.display_name} data={value} />
      </GridItem>
    );
  }
  return (
    <GridItem colSpan={isArray(value) ? 2 : 1} minW={0}>
      <Label>{attribute.display_name}</Label>
      <SummaryValue attribute={attribute} provider={provider} />
    </GridItem>
  );
};

const KeyFacts: FC<{ facts: DynamicAttribute[]; provider?: string }> = ({
  facts,
  provider,
}) => (
  <ScaleFade in initialScale={0.98}>
    <Wrap
      spacingX={8}
      spacingY={3}
      px={4}
      py={3}
      borderRadius="md"
      bg="gray.50"
    >
      {facts.map(fact => (
        <WrapItem key={fact.display_name} flexDirection="column" maxW="320px">
          <Label>{fact.display_name}</Label>
          <Box fontWeight={600}>
            <SummaryValue attribute={fact} provider={provider} />
          </Box>
        </WrapItem>
      ))}
    </Wrap>
  </ScaleFade>
);

const SectionBody: FC<{ section: SummarySection; provider?: string }> = ({
  section,
  provider,
}) => (
  <Stack spacing={4} pt={3}>
    {!!section.checks.length && <CheckBadges checks={section.checks} />}
    {!!section.fields.length && (
      <Grid templateColumns="repeat(4, 1fr)" gap={4}>
        {section.fields.map(attribute => (
          <Field
            key={attribute.display_name}
            attribute={attribute}
            provider={provider}
          />
        ))}
      </Grid>
    )}
  </Stack>
);

export const DynamicMetadata: FC = () => {
  const { metadataSummary } = useSelector(selectActiveResource);
  const { data: resource } = metadataSummary;
  const provider = resource?.provider;
  const [openSections, setOpenSections] = useState(readOpenSections);
  const [showEmpty, setShowEmpty] = useState(false);

  const attributes = useMemo(
    () =>
      orderBy(
        (resource?.metadata_attributes ?? []) as DynamicAttribute[],
        ['order'],
        ['asc'],
      ),
    [resource?.metadata_attributes],
  );
  const facts = attributes.filter(
    a => a.header && !isEmptyValue(a.attribute_value),
  );
  const sections = buildSections(attributes.filter(a => !facts.includes(a)));
  const empty = sections.flatMap(s => s.empty);
  // Resources without sections keep the flat layout, minus the empty fields.
  const flat = sections.length === 1 && !sections[0].checks.length;

  const toggle = (name: string) => {
    const next = openSections.includes(name)
      ? openSections.filter(s => s !== name)
      : [...openSections, name];
    setOpenSections(next);
    saveOpenSections(next);
  };

  return (
    <Stack spacing={5}>
      {!!facts.length && <KeyFacts facts={facts} provider={provider} />}
      {flat ? (
        <SectionBody section={sections[0]} provider={provider} />
      ) : (
        sections.map(section => {
          const open = openSections.includes(section.name);
          return (
            <Box
              key={section.name}
              borderBottom="1px solid"
              borderColor="componentBorder"
              pb={4}
            >
              <HStack
                as="button"
                w="full"
                justify="space-between"
                onClick={() => toggle(section.name)}
                aria-expanded={open}
              >
                <HStack spacing={2}>
                  <Text fontWeight={600}>{section.name}</Text>
                  <Text
                    px={2}
                    borderRadius="full"
                    bg={section.failing ? 'red.50' : 'gray.100'}
                    color={section.failing ? 'critical' : 'gray.600'}
                  >
                    {sectionSummary(section)}
                  </Text>
                </HStack>
                <Icon
                  as={FiChevronDown}
                  transform={open ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s"
                />
              </HStack>
              <Collapse in={open} animateOpacity>
                <SectionBody section={section} provider={provider} />
              </Collapse>
            </Box>
          );
        })
      )}
      {!!empty.length && (
        <Box>
          <Text
            as="button"
            color="primary"
            onClick={() => setShowEmpty(!showEmpty)}
          >
            {showEmpty
              ? 'Hide empty fields'
              : `Show ${empty.length} empty field${
                  empty.length > 1 ? 's' : ''
                }`}
          </Text>
          <Collapse in={showEmpty} animateOpacity>
            <Grid templateColumns="repeat(4, 1fr)" gap={4} pt={3}>
              {empty.map(attribute => (
                <GridItem key={attribute.display_name}>
                  <Label>{attribute.display_name}</Label>
                  <Text color={customTheme.colors.gray['250']}>-</Text>
                </GridItem>
              ))}
            </Grid>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
};
